/**
 * Listen to one S-05 run's tracking record.
 *
 * The run's `tracking/{runId}` document is the only source that is both LIVE and
 * DURABLE. agent-svc re-rolls it after every turn, whereas the project document's
 * `analysis.completedTurns` is written exactly twice — `0` when the run begins and
 * the final count when it ends — so a progress view fed from the project document
 * reads "0 of 7" for the whole twenty minutes while turns are completing.
 *
 * A deliberate hook rather than a fourth Redux channel. The three existing channels
 * (`engagements`, `goal`, `keyQuestions`) live for the whole page; this one lives
 * only while a run exists, and its lifetime is keyed on a `runId` that arrives
 * later than mount. Putting it in the slice would mean start/stop actions
 * threaded through the listener manager for state exactly one screen reads.
 *
 * Both subscriptions are torn down on `runId` change as well as on unmount — a
 * second run on the same project must not keep the first one's listeners.
 */

import { useEffect, useState } from 'react';
import { listenToRunTracking, listenToRunTurns } from '../../services/advisoryService';
import type { AdvisoryRawDoc } from '../../services/advisoryService';

export interface UseAdvisoryRunTrackingResult {
  /** The run document's raw data, or `null` before the first snapshot. */
  runDoc: Record<string, unknown> | null;
  /** One entry per turn record written so far. */
  turnDocs: Record<string, unknown>[];
  /**
   * A mapped error code from either subscription, or `null`.
   *
   * Surfaced rather than swallowed so a caller can tell "no run record" from
   * "could not read the run record" — the second is a rules problem and must not
   * render as an absent run.
   */
  errorCode: string | null;
}

const EMPTY: UseAdvisoryRunTrackingResult = { runDoc: null, turnDocs: [], errorCode: null };

export function useAdvisoryRunTracking(
  userId: string | undefined,
  projectId: string | undefined,
  runId: string | undefined
): UseAdvisoryRunTrackingResult {
  const [state, setState] = useState<UseAdvisoryRunTrackingResult>(EMPTY);

  useEffect(() => {
    if (!userId || !projectId || !runId) {
      // Reset rather than keep the previous run's data: without this, clearing the
      // run id would leave a stale record on screen that nothing is updating.
      setState(EMPTY);
      return undefined;
    }

    let live = true;
    const guard =
      <T>(apply: (value: T) => void) =>
      (value: T) => {
        if (live) {
          apply(value);
        }
      };

    const runSubscription = listenToRunTracking(
      userId,
      projectId,
      runId,
      guard((doc: AdvisoryRawDoc | null) => setState(p => ({ ...p, runDoc: doc?.data ?? null }))),
      guard((errorCode: string) => setState(p => ({ ...p, errorCode })))
    );
    const turnsSubscription = listenToRunTurns(
      userId,
      projectId,
      runId,
      guard((docs: AdvisoryRawDoc[]) => setState(p => ({ ...p, turnDocs: docs.map(d => d.data) }))),
      guard((errorCode: string) => setState(p => ({ ...p, errorCode })))
    );
    const unsubscribeRun =
      typeof runSubscription === 'function' ? runSubscription : () => undefined;
    const unsubscribeTurns =
      typeof turnsSubscription === 'function' ? turnsSubscription : () => undefined;

    return () => {
      live = false;
      unsubscribeRun();
      unsubscribeTurns();
    };
  }, [userId, projectId, runId]);

  return state;
}
